import { useCallback, useRef, useState, type ReactElement } from "react";
import { ReferenceArea } from "recharts";

import { chartSemanticColors } from "@/components/flexkit/charts/chart-colors";

/**
 * Hook that adds Grafana-style drag-to-zoom to any Recharts chart.
 *
 * Returns event handlers to spread onto the chart component and a
 * `referenceArea` element to render inside it. When the user completes
 * a drag, `onZoom` is called with the selected x-axis range.
 *
 * Works with both numeric x-axes (timestamps) and category x-axes
 * (date strings) — `activeLabel` from recharts is passed through.
 */

type RechartMouseEvent = {
    activeLabel?: string | number;
    activeTooltipIndex?: number;
    chartX?: number;
} | null;

type UseChartBrushZoomOptions = {
    /** Called when the user finishes a drag selection. Optional — when omitted the hook is inert. */
    onZoom?: (x1: string | number, x2: string | number) => void;
    /** yAxisId to attach the ReferenceArea to (needed for dual-axis charts). */
    yAxisId?: string;
};

type UseChartBrushZoomReturn = {
    /** Spread these onto the recharts chart component. */
    chartEvents: {
        onMouseDown: (e: RechartMouseEvent) => void;
        onMouseMove: (e: RechartMouseEvent) => void;
        onMouseUp: () => void;
        onMouseLeave: () => void;
    };
    /** Render this inside the chart as a child element. */
    referenceArea: ReactElement | null;
    /** True while the user is dragging — use to apply user-select: none. */
    isDragging: boolean;
};

export function useChartBrushZoom({ onZoom, yAxisId }: UseChartBrushZoomOptions): UseChartBrushZoomReturn {
    const [dragStart, setDragStart] = useState<string | number | null>(null);
    const [dragEnd, setDragEnd] = useState<string | number | null>(null);
    const isDraggingRef = useRef(false);
    const isDragging = dragStart != null;

    const onMouseDown = useCallback((e: RechartMouseEvent) => {
        if (e?.activeLabel != null) {
            isDraggingRef.current = true;
            setDragStart(e.activeLabel);
            setDragEnd(null);
        }
    }, []);

    const onMouseMove = useCallback((e: RechartMouseEvent) => {
        if (isDraggingRef.current && e?.activeLabel != null) {
            setDragEnd(e.activeLabel);
        }
    }, []);

    const onMouseUp = useCallback(() => {
        if (isDraggingRef.current && dragStart != null && dragEnd != null && dragStart !== dragEnd) {
            if (typeof dragStart === "number" && typeof dragEnd === "number") {
                onZoom?.(Math.min(dragStart, dragEnd), Math.max(dragStart, dragEnd));
            } else {
                onZoom?.(dragStart, dragEnd);
            }
        }
        isDraggingRef.current = false;
        setDragStart(null);
        setDragEnd(null);
    }, [dragStart, dragEnd, onZoom]);

    const onMouseLeave = useCallback(() => {
        isDraggingRef.current = false;
        setDragStart(null);
        setDragEnd(null);
    }, []);

    let referenceArea: ReactElement | null = null;
    if (dragStart != null && dragEnd != null && dragStart !== dragEnd) {
        const x1 =
            typeof dragStart === "number" && typeof dragEnd === "number" ? Math.min(dragStart, dragEnd) : dragStart;
        const x2 =
            typeof dragStart === "number" && typeof dragEnd === "number" ? Math.max(dragStart, dragEnd) : dragEnd;
        referenceArea = (
            <ReferenceArea
                x1={x1}
                x2={x2}
                fill={chartSemanticColors.accent}
                fillOpacity={0.15}
                stroke={chartSemanticColors.accent}
                strokeOpacity={0.4}
                style={{ pointerEvents: "none" }}
                {...(yAxisId != null ? { yAxisId } : {})}
            />
        );
    }

    return {
        chartEvents: { onMouseDown, onMouseMove, onMouseUp, onMouseLeave },
        referenceArea,
        isDragging,
    };
}

/**
 * Wraps an `onBrushZoom` callback so that category-axis date labels
 * (e.g. "Apr 9") are resolved to numeric timestamps using the chart data.
 *
 * Each data point must have a `date` (string) and `timestamp` (number) field.
 */
export function resolveDateLabelsToTimestamps(
    onZoom: (x1: string | number, x2: string | number) => void,
    data: { date: string; timestamp: number }[],
): (x1: string | number, x2: string | number) => void {
    return (x1, x2) => {
        const lookup = new Map(data.map((d) => [d.date, d.timestamp]));
        const t1 = typeof x1 === "string" ? lookup.get(x1) : x1;
        const t2 = typeof x2 === "string" ? lookup.get(x2) : x2;
        if (t1 != null && t2 != null) {
            onZoom(Math.min(t1, t2), Math.max(t1, t2));
        }
    };
}
